/**
 * Public Sentiment Report — modern per-section components (PSR).
 *
 * W1e editorial port (layout 12A — scale + source ledger):
 *   - Executive composes `EditorialScaleMeter` (favourable↔opposed gauge,
 *     value = sentimentIndex.score / 100) + `EditorialDefinitionList`
 *     (the structured sentiment facts) ABOVE the existing narrative prose.
 *   - The remaining seven narrative sections wrap their prose in
 *     `EditorialSubsection` so every preset renders the journal /
 *     institutional / monograph chrome instead of a bare paragraph.
 *
 * Post-W0, SectionShell's body wrapper already carries the themed prose ink
 * (roleColor 'body') + the report body font — the previous `SentimentBody`
 * roleColor override became redundant and was removed.
 */
import React from 'react';
import { admittedReportReferenceIndices, admittedSentimentLeadUrls, projectReportSourceEvidence } from '../../../../lib/reportGeneration/reportEvidenceSourceAdmission';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { CitedReportProse } from '../../report/CitedReportProse';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import {
  EditorialCallout,
  EditorialDefinitionList,
  EditorialScaleMeter,
  EditorialSubsection,
  type EditorialDefinitionItem,
} from '../../report/editorial';
import { resolveSectionCallout } from '../../../../lib/reportRendering/reportSectionCallouts';
import {
  GenerationEmissionsBlock,
  GenerationNoiseLightingBlock,
} from '../../report/GenerationImpactProjectionBlocks';
import type { ReportTheme } from '../../../../types/reportTheme';
import {
  psrHeadingSections,
  psrOmittedChapters,
  psrReportView,
  psrRetainedWithLimits,
  type PsrReportView,
} from '../../../../lib/sentiment/psrReportView';
import {
  CollectionReceiptLine,
  EvidenceLedger,
  NoRecordsNotice,
  PrintTableTwins,
  RetainedWithLimitsNotice,
  SentimentChangeStrip,
  SentimentExecutiveBlocks,
  SentimentOmissionList,
  SentimentTrendChart,
  TechSourceHeatmap,
  TechStanceBars,
} from './viz';

type Props = { report: SiteRagReport; label: string; sectionId?: string };

/**
 * Narrative paragraph with the [Ref N] citation walker. Ink + face are
 * inherited from the themed SectionShell body (W0) / EditorialSubsection
 * body — no per-paragraph colour override.
 */
const SentimentProse: React.FC<{ text: string; references: SiteRagReport['references'] }> = ({ text, references }) => (
  <CitedReportProse text={text} references={references} />
);

/**
 * PSR redesign — prose under a labelled sub-rule. Deterministic template
 * prose written as `### Heading` blocks becomes one labelled subsection per
 * heading (verbatim text, headings as labels); any other prose keeps the
 * single `label`. Used only on the psr-meta-v2 path.
 */
const PsrProse: React.FC<{ theme: ReportTheme; label: string; text: string; references: SiteRagReport['references'] }> = ({ theme, label, text, references }) => {
  const parts = psrHeadingSections(text);
  if (!parts) {
    return (
      <EditorialSubsection theme={theme} label={label}>
        <SentimentProse text={text} references={references} />
      </EditorialSubsection>
    );
  }
  return (
    <>
      {parts.map((part, i) => (
        <EditorialSubsection key={`${i}-${part.heading}`} theme={theme} label={part.heading}>
          <SentimentProse text={part.body} references={references} />
        </EditorialSubsection>
      ))}
    </>
  );
};

/**
 * PSR redesign (psr-meta-v2) — deterministic blocks a prose section shows
 * ABOVE its narrative. Called only when `psrReportView(report)` is defined,
 * so a legacy report never reaches it. Returns `label` to replace the prose
 * sub-label when the blocks carry the section's figures (the prose then
 * reads as the "Reading" of those figures).
 */
type PsrSectionBlocks = (args: { report: SiteRagReport; view: PsrReportView; theme: ReportTheme }) => { blocks: React.ReactNode; label?: string } | null;

/**
 * Factory for the seven prose sections. `editorialLabel` is the small-caps
 * sub-label on the EditorialSubsection rule — editorial chrome describing
 * the section's analytical lens (mirrors the Environmental RTU-2 pattern),
 * NOT generated data.
 */
function textSection(field: keyof SiteRagReport, editorialLabel: string, psrBlocks?: PsrSectionBlocks): React.FC<Props> {
  const Component: React.FC<Props> = ({ report: reportInput, label }) => {
    const report = projectReportSourceEvidence(reportInput);
    const theme = useEffectiveReportTheme();
    const raw = report[field];
    const text = typeof raw === 'string' ? raw.trim() : '';
    const hasData = text.length > 0;
    const view = psrBlocks ? psrReportView(report) : undefined;
    const extra = view && psrBlocks ? psrBlocks({ report, view, theme }) : null;
    const proseLabel = extra?.label ?? (field === 'stakeholderMap' && report.publicSentimentStatus === 'unavailable'
      ? 'Source Contacts & Outreach Roles' : editorialLabel);
    return (
      <SectionShell label={label} hasData={hasData} report={report}>
        {hasData && extra?.blocks}
        {hasData && (view
          ? <PsrProse theme={theme} label={proseLabel} text={text} references={report.references} />
          : (
            <EditorialSubsection theme={theme} label={proseLabel}>
              <SentimentProse text={text} references={report.references} />
            </EditorialSubsection>
          ))}
      </SectionShell>
    );
  };
  Component.displayName = `PSR_${String(field)}`;
  return Component;
}

/** Contacts / next actions kept with limits: the omission detail, verbatim, above the prose. */
const retainedNotice = (sectionId: 'stakeholderMap' | 'mitigationPlaybook'): PsrSectionBlocks => ({ report, theme }) => {
  const detail = psrRetainedWithLimits(report, sectionId);
  return detail ? { blocks: <RetainedWithLimitsNotice theme={theme} sectionId={sectionId} detail={detail} /> } : null;
};

const psrStack = (children: React.ReactNode) => (
  <div data-psr-blocks style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>{children}</div>
);

const finiteNumber = (n: number | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n);

/**
 * Executive — layout 12A (scale + source ledger). The gauge + structured
 * facts compose ABOVE the narrative (Exec-Summary D1 proof pattern); the
 * section's hasData gate stays prose-driven, identical to the pre-port
 * factory semantics.
 */
/** Human label for each geographic scan layer in the signal ledger. */
const SCOPE_FACT_LABEL: Record<string, string> = {
  site: 'Local sentiment (site/county)',
  state: 'Statewide sentiment',
  country: 'National sentiment',
};
/** Polarity word matching the SENTIMENT-INDEX vocabulary, ±0.2 band. */
function polarityWord(mean: number): string {
  if (mean <= -0.2) return 'oppositional';
  if (mean >= 0.2) return 'supportive';
  return 'mixed';
}

export const ExecutiveSection: React.FC<Props> = ({ report: reportInput, label }) => {
    const report = projectReportSourceEvidence(reportInput);
  const theme = useEffectiveReportTheme();
  const text = typeof report.executive === 'string' ? report.executive.trim() : '';
  const hasData = text.length > 0;

  const idx = report.sentimentIndex;
  const meta = report.publicSentimentMeta;
  // GAP (12A): the gauge needs the REAL 0-100 sentimentIndex.score (PSR-2
  // composer). When the score is absent the meter is OMITTED entirely —
  // never derive a marker position from `band` or fabricate one.
  const rawScore = idx?.score;
  const score = finiteNumber(rawScore) ? rawScore : undefined;
  const band = typeof idx?.band === 'string' ? idx.band.trim() : '';
  const topDrivers = (idx?.topDrivers ?? [])
    .map(d => (typeof d === 'string' ? d.trim() : ''))
    .filter(Boolean);

  // Structured sentiment facts — empty inputs are dropped, never placeholdered.
  const facts: EditorialDefinitionItem[] = [];
  if (score !== undefined) {
    facts.push({ label: 'Sentiment index', value: `${Math.round(score)} / 100` });
  }
  if (band) {
    facts.push({
      label: 'Band',
      value: <span style={{ textTransform: 'capitalize' }}>{band}</span>,
    });
  }
  if (topDrivers.length > 0) {
    facts.push({ label: 'Top drivers', value: topDrivers.join(' · ') });
  }
  // Geographic-layer rollup (site / state / country) — surface each layer
  // distinctly so the report shows the three-layer scan rather than a single
  // collapsed total. A quiet site reads "no record observed" locally while
  // still carrying a statewide + national signal.
  const perScope = Array.isArray(meta?.perScope) ? meta!.perScope : [];
  for (const s of perScope) {
    facts.push({
      label: SCOPE_FACT_LABEL[s.scope] ?? s.scope,
      value: s.count > 0 && (s.meanConfidence ?? 0) > 0
        ? `${s.count} obs · ${s.meanSentiment >= 0 ? '+' : ''}${s.meanSentiment.toFixed(2)} (${polarityWord(s.meanSentiment)})`
        : s.count > 0 ? `${s.count} records · stance not established` : 'no public record observed',
    });
  }
  // A legacy discovery row is not proof that its page was read. Reuse the
  // same exact frozen-reference/retained-payload admission as native exports.
  const admittedLeadUrls = admittedSentimentLeadUrls(report);
  for (const lead of meta?.sourceLeads ?? []) {
    if (!admittedLeadUrls.has(lead.url)) continue;
    facts.push({ label: 'Unverified source lead', value: <span><a href={lead.url} target="_blank" rel="noreferrer">{lead.title}</a> · {lead.publicationDate ?? 'publication date unknown'} · {lead.limitations.join(' ')}</span> });
  }
  if (meta?.assessment) {
    facts.push({ label: 'Local evidence', value: meta.assessment.reason });
    if (meta.assessment.monthly.length) facts.push({ label: 'Observed monthly trend', value: meta.assessment.monthly.map(row => `${row.month}: ${row.supportIndex}/100 (${row.count} observations)`).join(' · ') });
    if (meta.change) facts.push({ label: 'Change since prior scan', value: `${meta.change.addedObservationCount} new records${meta.change.supportIndexDelta !== undefined ? `; local index ${meta.change.supportIndexDelta >= 0 ? '+' : ''}${meta.change.supportIndexDelta}` : '; no comparable local index'}` });
  }
  const entryCount = meta?.entryCount;
  if (finiteNumber(entryCount)) {
    facts.push({ label: 'Entries analysed', value: String(entryCount) });
  }
  const sourcesAttempted = meta?.sourcesAttempted;
  if (finiteNumber(sourcesAttempted)) {
    facts.push({ label: 'Sources attempted', value: String(sourcesAttempted) });
  }
  const opposedTechCount = meta?.opposedTechCount;
  if (finiteNumber(opposedTechCount)) {
    facts.push({ label: 'Opposed technologies', value: String(opposedTechCount) });
  }
  const coverageStatus = typeof meta?.status === 'string' ? meta.status.trim() : '';
  if (coverageStatus) {
    facts.push({
      label: 'Coverage status',
      value: <span style={{ textTransform: 'capitalize' }}>{coverageStatus}</span>,
    });
  }

  const callout = resolveSectionCallout(report, 'publicSentimentExecutive');

  // PSR redesign — a report carrying the psr-meta-v2 projection renders the
  // deterministic hero / status block, KPI rail and coverage disclosure in
  // place of the definition list (whose per-scope polarity words mixed
  // context with local stance). Legacy reports take the unchanged path below.
  const view = psrReportView(report);
  const refreshNotice = !report.publicSentimentResearch ? (
    <div className="print:hidden" data-testid="sentiment-research-refresh-available">
      <EditorialCallout theme={theme} label="Refresh available">
        You can rerun this report to collect cited public-source research.
      </EditorialCallout>
    </div>
  ) : null;
  if (view) {
    const omitted = psrOmittedChapters(report);
    return (
      <SectionShell label={label} hasData={hasData} report={report}>
        {hasData && (
          <>
            {refreshNotice}
            <SentimentExecutiveBlocks
              theme={theme}
              view={view}
              reason={meta?.assessment?.reason}
              entryCount={finiteNumber(meta?.entryCount) ? meta!.entryCount : undefined}
            />
            {callout && !view.scan && (
              <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
                {callout.body}
              </EditorialCallout>
            )}
            <PsrProse theme={theme} label={view.state === 'assessed' ? 'Summary' : 'Screening outcome'} text={text} references={report.references} />
            {omitted.length > 0 && <SentimentOmissionList theme={theme} groups={omitted} />}
          </>
        )}
      </SectionShell>
    );
  }

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      {hasData && (
        <>
          {refreshNotice}
          {score !== undefined && (
            <div data-testid="sentiment-index-gauge">
              <EditorialScaleMeter
                theme={theme}
                variant="gradient"
                value={score / 100}
                ticks={['Opposed', 'Mixed', 'Supportive']}
              />
            </div>
          )}
          {facts.length > 0 && (
            <div data-testid="sentiment-signal-ledger">
              <EditorialDefinitionList theme={theme} items={facts} />
            </div>
          )}
          {callout && (
            <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
              {callout.body}
            </EditorialCallout>
          )}
          <SentimentProse text={text} references={report.references} />
        </>
      )}
    </SectionShell>
  );
};
ExecutiveSection.displayName = 'PSR_executive';

export const StakeholderMapSection = textSection('stakeholderMap', 'Stakeholder Tiers & Positions', retainedNotice('stakeholderMap'));
export const PerBtmSentimentSection = textSection('perBtmSentiment', 'Technology-Specific Reception', ({ view, theme }) => (
  view.index === undefined ? null : {
    blocks: psrStack(<><TechStanceBars theme={theme} view={view} /><TechSourceHeatmap theme={theme} view={view} /></>),
    label: 'Reading',
  }
));
export const ThemeDeepDivesSection: React.FC<Props> = ({ report: reportInput, label }) => {
    const report = projectReportSourceEvidence(reportInput);
  const theme = useEffectiveReportTheme();
  const text = typeof report.themeDeepDives === 'string' ? report.themeDeepDives.trim() : '';
  const hasPromotedScenario = !!report.generationImpactProjection?.environmentalAndCommunity;
  const hasData = text.length > 0 || hasPromotedScenario;
  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      {hasPromotedScenario && (
        <EditorialCallout theme={theme} tone="accent" label="Objective promoted-scenario basis">
          These deterministic operating impacts are planning inputs for community engagement. They are not public-opinion observations or a sentiment score.
        </EditorialCallout>
      )}
      <GenerationEmissionsBlock report={report} />
      <GenerationNoiseLightingBlock report={report} />
      {text.length > 0 && (
        <EditorialSubsection theme={theme} label="Dominant Themes">
          <SentimentProse text={text} references={report.references} />
        </EditorialSubsection>
      )}
    </SectionShell>
  );
};
ThemeDeepDivesSection.displayName = 'PSR_themeDeepDives';
export const TrendAnalysisSection = textSection('trendAnalysis', '12-Month Trajectory', ({ view, theme }) => (
  view.index === undefined ? null : {
    blocks: psrStack(<><SentimentChangeStrip theme={theme} view={view} /><SentimentTrendChart theme={theme} view={view} /></>),
    label: 'Reading',
  }
));
export const ComparableSiteBenchmarkSection = textSection('comparableSiteBenchmark', 'Peer-Site Forecasting Anchor');
export const MitigationPlaybookSection = textSection('mitigationPlaybook', 'Theme-Anchored Actions', retainedNotice('mitigationPlaybook'));

export const SentimentAppendixSection: React.FC<Props> = ({ report: reportInput, label }) => {
    const report = projectReportSourceEvidence(reportInput);
  const theme = useEffectiveReportTheme();
  const text = (report.sentimentAppendix ?? '').trim();
  const hasData = text.length > 0;
  // PSR redesign — the evidence ledger and the chart tables lead the
  // inventory; the stored appendix prose (method and limits, or the
  // deterministic receipt) follows unchanged.
  const view = psrReportView(report);
  if (view) {
    const coverageOnly = view.state === 'coverage-only';
    return (
      <SectionShell label={label} hasData={hasData} report={report}>
        {hasData && (
          <div data-psr-blocks style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            {coverageOnly && (report.publicSentimentMeta?.entryCount ?? 0) === 0 && <NoRecordsNotice theme={theme} />}
            <EvidenceLedger theme={theme} view={view} references={report.references} visibleRefIndices={admittedReportReferenceIndices(report)} />
            <PsrProse
              theme={theme}
              label={coverageOnly ? 'Collection receipt' : view.state === 'assessed' ? 'Method and limits' : 'Source-Grouped Evidence Inventory'}
              text={text}
              references={report.references}
            />
            <PrintTableTwins theme={theme} view={view} />
            {!coverageOnly && <CollectionReceiptLine theme={theme} view={view} />}
          </div>
        )}
      </SectionShell>
    );
  }
  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      {hasData && (
        <EditorialSubsection theme={theme} label="Source-Grouped Evidence Inventory">
          <SentimentProse text={text} references={report.references} />
        </EditorialSubsection>
      )}
    </SectionShell>
  );
};
